/* global google, gapi */
import React, { useEffect, useState } from "react";
import { gapi } from "gapi-script";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { getDriveCredentials } from "../../services/driveCredentialService";
import DriveMenu from "./DriveMenu";

const DriveHome = () => {
  const [accessToken, setAccessToken] = useState(null);
  const [files, setFiles] = useState([]);
  const [folderStack, setFolderStack] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isListVisible, setIsListVisible] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [credentials, setCredentials] = useState(null);

  const folderId =
    folderStack.length > 0 ? folderStack[folderStack.length - 1].id : "root";

  
  useEffect(() => {
    const domains = [
      "https://docs.google.com",
      "https://ssl.gstatic.com",
      "https://lh3.googleusercontent.com",
      "https://drive.google.com",
    ];
    domains.forEach((href) => {
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = href;
      document.head.appendChild(link);
    });
  }, []);

 
  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        const data = await getDriveCredentials();
        if (data.length > 0) setCredentials(data[0]);
        else console.error("No credentials found in database.");
      } catch (err) {
        console.error("Error fetching drive credentials:", err);
      }
    };
    fetchCredentials();
  }, []);


  useEffect(() => {
    if (!credentials) return;

    gapi.load("client", async () => {
      await gapi.client.init({
        apiKey: credentials.apiKey,
        discoveryDocs: [
          "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
        ],
      });

      const savedToken = localStorage.getItem("drive_access_token");
      const savedExpiry = localStorage.getItem("drive_token_expiry");

      if (savedToken && savedExpiry && Date.now() < parseInt(savedExpiry)) {
        gapi.client.setToken({ access_token: savedToken });
        setAccessToken(savedToken);
        await Promise.all([
          gapi.client.drive.files.list({ pageSize: 1 }),
          fetch("https://docs.google.com", { mode: "no-cors" }),
        ]);
        listFiles();
      }
    });
  }, [credentials, folderId]);

  
  useEffect(() => {
    if (accessToken) {
      const iframe = document.createElement("iframe");
      iframe.src = "https://docs.google.com/document/create?embedded=true";
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 8000);
    }
  }, [accessToken]);


  const handleAuthClick = async () => {
    if (!credentials) return alert("Drive credentials not loaded.");
    const codeClient = google.accounts.oauth2.initCodeClient({
      client_id: credentials.clientId,
      scope: credentials.scopes,
      ux_mode: "popup",
      access_type: "offline",
      callback: async (response) => {
        if (response.code) {
          const tokenResponse = await fetch(
            "https://oauth2.googleapis.com/token",
            {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body: new URLSearchParams({
                code: response.code,
                client_id: credentials.clientId,
                client_secret: credentials.clientSecret,
                redirect_uri: "postmessage",
                grant_type: "authorization_code",
              }),
            }
          );
          const data = await tokenResponse.json();
          if (data.access_token) {
            gapi.client.setToken({ access_token: data.access_token });
            setAccessToken(data.access_token);
            localStorage.setItem("drive_access_token", data.access_token);
            localStorage.setItem(
              "drive_token_expiry",
              Date.now() + data.expires_in * 1000
            );
            await fetch("https://docs.google.com", { mode: "no-cors" });
            listFiles();
          }
        }
      },
    });
    codeClient.requestCode();
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("drive_access_token");
    if (token) {
      await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
        method: "POST",
        headers: { "Content-type": "application/x-www-form-urlencoded" },
      });
    }
    localStorage.clear();
    gapi.client.setToken(null);
    setAccessToken(null);
    setFiles([]);
    setSelectedFile(null);
    setPreviewFile(null);
    setFolderStack([]);
    setImageUrl(null);
    setIsListVisible(true);
  };


  const listFiles = async () => {
    try {
      let query = `'${folderId}' in parents and trashed=false`;
      if (searchName) query += ` and name contains '${searchName}'`;
      if (dateFrom)
        query += ` and modifiedTime >= '${new Date(dateFrom).toISOString()}'`;
      if (dateTo)
        query += ` and modifiedTime <= '${new Date(dateTo).toISOString()}'`;

      const res = await gapi.client.drive.files.list({
        q: query,
        fields: "files(id, name, mimeType, modifiedTime, webViewLink)",
        orderBy: "modifiedTime desc",
      });
      setFiles(res.result.files || []);
      setPreviewFile(null);
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  };

  const createFolder = async () => {
    const name = prompt("Enter folder name:");
    if (!name) return;
    try {
      const res = await gapi.client.drive.files.create({
        resource: {
          name,
          mimeType: "application/vnd.google-apps.folder",
          parents: [folderId],
        },
        fields: "id, name",
      });
      alert(`📁 Folder created: ${res.result.name}`);
      openFolder(res.result.id, res.result.name);
    } catch (err) {
      console.error("Error creating folder:", err);
    }
  };


  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    let mimeType = file.type;
    if (file.name.endsWith(".doc") || file.name.endsWith(".docx"))
      mimeType = "application/vnd.google-apps.document";
    else if (file.name.endsWith(".xls") || file.name.endsWith(".xlsx"))
      mimeType = "application/vnd.google-apps.spreadsheet";

    const metadata = { name: file.name, mimeType, parents: [folderId] };
    const form = new FormData();
    form.append(
      "metadata",
      new Blob([JSON.stringify(metadata)], { type: "application/json" })
    );
    form.append("file", file);

    await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
      {
        method: "POST",
        headers: new Headers({ Authorization: "Bearer " + accessToken }),
        body: form,
      }
    );
    listFiles();
  };


  const handleFileOpenInline = async (file) => {
    if (file.mimeType === "application/vnd.google-apps.document") {
      setSelectedFile(file);
      setIsListVisible(false);
      return;
    }

    if (file.name.endsWith(".docx") || file.name.endsWith(".doc")) {
      try {
        const res = await gapi.client.drive.files.copy({
          fileId: file.id,
          resource: {
            mimeType: "application/vnd.google-apps.document",
            name: file.name.replace(/\.[^/.]+$/, ""),
          },
          fields: "id, name, mimeType",
        });
        const newFile = res.result;
        await gapi.client.drive.files.delete({ fileId: file.id });
        listFiles();
        setSelectedFile(newFile);
        setIsListVisible(false);
      } catch (err) {
        console.error("Error converting file:", err);
      }
      return;
    }

    if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      try {
        const res = await gapi.client.drive.files.copy({
          fileId: file.id,
          resource: {
            mimeType: "application/vnd.google-apps.spreadsheet",
            name: file.name.replace(/\.[^/.]+$/, ""),
          },
          fields: "id, name, mimeType",
        });
        const newFile = res.result;
        await gapi.client.drive.files.delete({ fileId: file.id });
        listFiles();
        setSelectedFile(newFile);
        setIsListVisible(false);
      } catch (err) {
        console.error("Error converting file:", err);
      }
      return;
    }

    setSelectedFile(file);
    setIsListVisible(false);
  };

  const openFolder = (id, name) => {
    setFolderStack([...folderStack, { id, name }]);
    setPreviewFile(null);
    setSelectedFile(null);
    setIsListVisible(true);
  };

  const goBack = () => {
    const updated = [...folderStack];
    updated.pop();
    setFolderStack(updated);
    setSelectedFile(null);
    setIsListVisible(true);
  };

  const fetchImageUrl = async (id) => {
    try {
      const res = await fetch(
        `https://www.googleapis.com/drive/v3/files/${id}?alt=media`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const blob = await res.blob();
      setImageUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error("Error fetching image:", err);
    }
  };


  const renderFilePreview = () => {
    if (!selectedFile)
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          Select a file to preview or edit
        </div>
      );

    const { mimeType, id, name } = selectedFile;
    const iframeClass =
      "w-full h-[calc(100vh-120px)] border-none rounded-lg shadow bg-white";

    if (
      mimeType === "application/vnd.google-apps.document" ||
      name.endsWith(".docx") ||
      name.endsWith(".doc")
    )
      return (
        <iframe
          src={`https://docs.google.com/document/d/${id}/edit?embedded=true`}
          title="Docs"
          className={iframeClass}
        />
      );

    if (
      mimeType === "application/vnd.google-apps.spreadsheet" ||
      name.endsWith(".xlsx") ||
      name.endsWith(".xls")
    )
      return (
        <iframe
          src={`https://docs.google.com/spreadsheets/d/${id}/edit?embedded=true`}
          title="Sheets"
          className={iframeClass}
        />
      );

    if (mimeType.includes("pdf"))
      return (
        <iframe
          src={`https://drive.google.com/file/d/${id}/preview`}
          title="PDF"
          className={iframeClass}
        />
      );

    if (mimeType.startsWith("image/")) {
      fetchImageUrl(id);
      return (
        <div className="flex items-center justify-center w-full h-[calc(100vh-120px)] bg-gray-100">
          {imageUrl ? (
            <Zoom>
              <img
                src={imageUrl}
                alt={name}
                className="object-contain max-w-full max-h-full rounded-lg shadow-md cursor-zoom-in"
              />
            </Zoom>
          ) : (
            <div className="text-gray-400">Loading image...</div>
          )}
        </div>
      );
    }

    return (
      <iframe
        src={`https://drive.google.com/file/d/${id}/preview`}
        title="Preview"
        className={iframeClass}
      />
    );
  };

  const breadcrumb =
    folderStack.length === 0
      ? "📂 Root"
      : ["📂 Root", ...folderStack.map((f) => f.name)].join(" / ");

  const renderTopButtons = () => {
    if (!accessToken)
      return (
        <button
          onClick={handleAuthClick}
          className="px-4 py-1.5 text-white bg-blue-600 rounded hover:bg-blue-700"
        >
          Connect Drive
        </button>
      );

    if (!isListVisible)
      return (
        <div className="flex gap-2">
          <button
            onClick={goBack}
            className="px-3 py-1 text-sm text-white bg-gray-600 rounded hover:bg-gray-700"
          >
            ← Back
          </button>
          <button
            onClick={() => setIsListVisible(true)}
            className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            OPEN LIST
          </button>
        </div>
      );

    return (
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleLogout}
          className="px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700"
        >
          Logout
        </button>
        {folderStack.length > 0 && (
          <button
            onClick={goBack}
            className="px-3 py-1 text-sm text-white bg-gray-600 rounded hover:bg-gray-700"
          >
            ← Back
          </button>
        )}
        <button
          onClick={listFiles}
          className="px-3 py-1 text-sm text-white bg-indigo-600 rounded hover:bg-indigo-700"
        >
          Refresh
        </button>
        <button
          onClick={createFolder}
          className="px-3 py-1 text-sm text-white bg-green-600 rounded hover:bg-green-700"
        >
          + Folder
        </button>
        <button
          onClick={() => document.getElementById("fileInput").click()}
          className="px-3 py-1 text-sm text-white bg-yellow-500 rounded hover:bg-yellow-600"
        >
          Upload
        </button>
        <input id="fileInput" type="file" hidden onChange={handleFileChange} />
      </div>
    );
  };

  return (
    <div className="flex w-full h-screen overflow-hidden bg-gray-50">
      {isListVisible && <DriveMenu />}
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between px-4 py-3 bg-white border-b shadow-sm">
          <h2 className="text-lg font-semibold text-gray-700">
            {isListVisible
              ? "Drive Explorer"
              : selectedFile?.name || "Drive Viewer"}
          </h2>
          {renderTopButtons()}
        </div>

        {isListVisible ? (
          <div className="flex flex-1 overflow-hidden">
            <div className="w-[260px] bg-white border-r overflow-y-auto text-sm">
              <div className="p-3 text-xs text-gray-500 truncate border-b">
                {breadcrumb}
              </div>
              {files.length ? (
                <ul>
                  {files.map((file) => (
                    <li
                      key={file.id}
                      onClick={() => setPreviewFile(file)}
                      onDoubleClick={() =>
                        file.mimeType ===
                        "application/vnd.google-apps.folder"
                          ? openFolder(file.id, file.name)
                          : handleFileOpenInline(file)
                      }
                      className={`px-4 py-2 border-b cursor-pointer truncate ${
                        previewFile?.id === file.id
                          ? "bg-gray-200 font-medium"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {file.mimeType ===
                      "application/vnd.google-apps.folder"
                        ? "📁"
                        : "📄"}{" "}
                      {file.name}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  No files found
                </div>
              )}
            </div>
            <div className="flex-1 overflow-hidden bg-gray-100">
              {previewFile ? (
                <iframe
                  src={`https://drive.google.com/file/d/${previewFile.id}/preview`}
                  title="Preview"
                  className="w-full h-full border-none"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  Select a file to preview
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 bg-gray-100">{renderFilePreview()}</div>
        )}
      </div>
    </div>
  );
};

export default DriveHome;








////=========>>> Old One

// /* global google, gapi */
// import React, { useEffect, useState } from "react";
// import { gapi } from "gapi-script";
// import Zoom from "react-medium-image-zoom";
// import "react-medium-image-zoom/dist/styles.css";
// import { getDriveCredentials } from "../../services/driveCredentialService";
// import DriveMenu from "./DriveMenu";

// const DriveHome = () => {
//   const [accessToken, setAccessToken] = useState(null);
//   const [files, setFiles] = useState([]);
//   const [folderStack, setFolderStack] = useState([]);
//   const [selectedFile, setSelectedFile] = useState(null);
//   const [isListVisible, setIsListVisible] = useState(true);
//   const [previewFile, setPreviewFile] = useState(null);
//   const [imageUrl, setImageUrl] = useState(null);
//   const [searchName, setSearchName] = useState("");
//   const [dateFrom, setDateFrom] = useState("");
//   const [dateTo, setDateTo] = useState("");
//   const [credentials, setCredentials] = useState(null);

//   const folderId =
//     folderStack.length > 0 ? folderStack[folderStack.length - 1].id : "root";

//   // ✅ Fetch credentials dynamically from backend
//   useEffect(() => {
//     const fetchCredentials = async () => {
//       try {
//         const data = await getDriveCredentials();
//         if (data.length > 0) {
//           setCredentials(data[0]);
//         } else {
//           console.error("No credentials found in database.");
//         }
//       } catch (err) {
//         console.error("Error fetching drive credentials:", err);
//       }
//     };

//     fetchCredentials();
//   }, []);

//   // ✅ Initialize GAPI after credentials are loaded
//   useEffect(() => {
//     if (!credentials) return;

//     gapi.load("client", async () => {
//       await gapi.client.init({
//         apiKey: credentials.apiKey,
//         discoveryDocs: [
//           "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
//         ],
//       });

//       const savedToken = localStorage.getItem("drive_access_token");
//       const savedExpiry = localStorage.getItem("drive_token_expiry");

//       if (savedToken && savedExpiry && Date.now() < parseInt(savedExpiry)) {
//         gapi.client.setToken({ access_token: savedToken });
//         setAccessToken(savedToken);
//         listFiles();
//       }
//     });
//   }, [credentials, folderId]);

//   // ✅ Authentication
//   const handleAuthClick = async () => {
//     if (!credentials) return alert("Drive credentials not loaded.");

//     const codeClient = google.accounts.oauth2.initCodeClient({
//       client_id: credentials.clientId,
//       scope: credentials.scopes,
//       ux_mode: "popup",
//       access_type: "offline",
//       callback: async (response) => {
//         if (response.code) {
//           const tokenResponse = await fetch(
//             "https://oauth2.googleapis.com/token",
//             {
//               method: "POST",
//               headers: { "Content-Type": "application/x-www-form-urlencoded" },
//               body: new URLSearchParams({
//                 code: response.code,
//                 client_id: credentials.clientId,
//                 client_secret: credentials.clientSecret,
//                 redirect_uri: "postmessage",
//                 grant_type: "authorization_code",
//               }),
//             }
//           );

//           const data = await tokenResponse.json();
//           if (data.access_token) {
//             gapi.client.setToken({ access_token: data.access_token });
//             setAccessToken(data.access_token);
//             localStorage.setItem("drive_access_token", data.access_token);
//             localStorage.setItem(
//               "drive_token_expiry",
//               Date.now() + data.expires_in * 1000
//             );
//             listFiles();
//           }
//         }
//       },
//     });

//     codeClient.requestCode();
//   };

//   const handleLogout = async () => {
//     const token = localStorage.getItem("drive_access_token");
//     if (token) {
//       await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
//         method: "POST",
//         headers: { "Content-type": "application/x-www-form-urlencoded" },
//       });
//     }
//     localStorage.clear();
//     gapi.client.setToken(null);
//     setAccessToken(null);
//     setFiles([]);
//     setSelectedFile(null);
//     setPreviewFile(null);
//     setFolderStack([]);
//     setImageUrl(null);
//     setIsListVisible(true);
//   };

//   const listFiles = async () => {
//     try {
//       let query = `'${folderId}' in parents and trashed=false`;

//       if (searchName) query += ` and name contains '${searchName}'`;
//       if (dateFrom)
//         query += ` and modifiedTime >= '${new Date(dateFrom).toISOString()}'`;
//       if (dateTo)
//         query += ` and modifiedTime <= '${new Date(dateTo).toISOString()}'`;

//       const res = await gapi.client.drive.files.list({
//         q: query,
//         fields: "files(id, name, mimeType, modifiedTime, webViewLink)",
//         orderBy: "modifiedTime desc",
//       });
//       setFiles(res.result.files || []);
//       setPreviewFile(null);
//     } catch (err) {
//       console.error("Error fetching files:", err);
//     }
//   };

//   const createFolder = async () => {
//     const name = prompt("Enter folder name:");
//     if (!name) return;

//     try {
//       const res = await gapi.client.drive.files.create({
//         resource: {
//           name,
//           mimeType: "application/vnd.google-apps.folder",
//           parents: [folderId],
//         },
//         fields: "id, name",
//       });
//       alert(`📁 Folder created: ${res.result.name}`);
//       openFolder(res.result.id, res.result.name);
//     } catch (err) {
//       console.error("Error creating folder:", err);
//       alert("❌ Failed to create folder. Check console for details.");
//     }
//   };

//   const fetchImageUrl = async (id) => {
//     try {
//       const res = await fetch(
//         `https://www.googleapis.com/drive/v3/files/${id}?alt=media`,
//         {
//           headers: { Authorization: `Bearer ${accessToken}` },
//         }
//       );
//       const blob = await res.blob();
//       const url = URL.createObjectURL(blob);
//       setImageUrl(url);
//     } catch (err) {
//       console.error("Error fetching image:", err);
//     }
//   };

//   const openFolder = (id, name) => {
//     setFolderStack([...folderStack, { id, name }]);
//     setPreviewFile(null);
//     setSelectedFile(null);
//     setIsListVisible(true);
//   };

//   const goBack = () => {
//     const updated = [...folderStack];
//     updated.pop();
//     setFolderStack(updated);
//     setSelectedFile(null);
//     setIsListVisible(true);
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;
//     const metadata = { name: file.name, parents: [folderId] };
//     const form = new FormData();
//     form.append(
//       "metadata",
//       new Blob([JSON.stringify(metadata)], { type: "application/json" })
//     );
//     form.append("file", file);
//     fetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart", {
//       method: "POST",
//       headers: new Headers({ Authorization: "Bearer " + accessToken }),
//       body: form,
//     }).then(() => listFiles());
//   };

//   const renderFilePreview = () => {
//     if (!selectedFile)
//       return (
//         <div className="flex items-center justify-center h-full text-gray-400">
//           Select a file to preview or edit
//         </div>
//       );

//     const { mimeType, id, name } = selectedFile;
//     const iframeClass =
//       "w-full h-[calc(100vh-120px)] border-none rounded-lg shadow bg-white";

//     if (
//       mimeType === "application/vnd.google-apps.document" ||
//       name.endsWith(".docx") ||
//       name.endsWith(".doc")
//     )
//       return (
//         <iframe
//           src={`https://docs.google.com/document/d/${id}/edit?usp=drivesdk`}
//           title="Google Docs Editor"
//           className={iframeClass}
//         />
//       );

//     if (
//       mimeType === "application/vnd.google-apps.spreadsheet" ||
//       name.endsWith(".xlsx") ||
//       name.endsWith(".xls")
//     )
//       return (
//         <iframe
//           src={`https://docs.google.com/spreadsheets/d/${id}/edit?usp=drivesdk`}
//           title="Google Sheets Editor"
//           className={iframeClass}
//         />
//       );

//     if (mimeType.includes("pdf"))
//       return (
//         <iframe
//           src={`https://drive.google.com/file/d/${id}/preview`}
//           title="PDF Preview"
//           className={iframeClass}
//         />
//       );

//     if (mimeType.startsWith("image/")) {
//       fetchImageUrl(id);
//       return (
//         <div className="flex items-center justify-center w-full h-[calc(100vh-120px)] bg-gray-100">
//           {imageUrl ? (
//             <Zoom>
//               <img
//                 src={imageUrl}
//                 alt={name}
//                 className="object-contain max-w-full max-h-full rounded-lg shadow-md cursor-zoom-in"
//               />
//             </Zoom>
//           ) : (
//             <div className="text-gray-400">Loading image...</div>
//           )}
//         </div>
//       );
//     }

//     return (
//       <div className="text-sm italic text-gray-400">Preview unavailable</div>
//     );
//   };

//   const renderPreviewFile = (file) => {
//     if (!file)
//       return (
//         <div className="flex items-center justify-center h-full text-gray-400">
//           No files found in this folder
//         </div>
//       );

//     const { mimeType, id, name } = file;
//     const iframeMini = "w-full h-full border-none bg-white";

//     if (
//       mimeType === "application/vnd.google-apps.document" ||
//       name.endsWith(".docx") ||
//       name.endsWith(".doc")
//     )
//       return (
//         <iframe
//           src={`https://docs.google.com/document/d/${id}/preview`}
//           title="Preview"
//           className={iframeMini}
//         />
//       );

//     if (
//       mimeType === "application/vnd.google-apps.spreadsheet" ||
//       name.endsWith(".xlsx") ||
//       name.endsWith(".xls")
//     )
//       return (
//         <iframe
//           src={`https://docs.google.com/spreadsheets/d/${id}/preview`}
//           title="Preview"
//           className={iframeMini}
//         />
//       );

//     if (mimeType.includes("pdf"))
//       return (
//         <iframe
//           src={`https://drive.google.com/file/d/${id}/preview`}
//           title="PDF Preview"
//           className={iframeMini}
//         />
//       );

//     if (mimeType.startsWith("image/")) {
//       fetchImageUrl(id);
//       return (
//         <div className="flex items-center justify-center w-full h-full bg-gray-100">
//           {imageUrl ? (
//             <Zoom>
//               <img
//                 src={imageUrl}
//                 alt={name}
//                 className="object-contain max-w-full max-h-full rounded-lg shadow-md cursor-zoom-in"
//               />
//             </Zoom>
//           ) : (
//             <div className="text-gray-400">Loading image...</div>
//           )}
//         </div>
//       );
//     }

//     return (
//       <div className="text-sm italic text-gray-400">Preview unavailable</div>
//     );
//   };

//   const breadcrumb =
//     folderStack.length === 0
//       ? "📂 Root"
//       : ["📂 Root", ...folderStack.map((f) => f.name)].join(" / ");

//   const renderTopButtons = () => {
//     if (!accessToken)
//       return (
//         <button
//           onClick={handleAuthClick}
//           className="px-4 py-1.5 text-white bg-blue-600 rounded hover:bg-blue-700"
//         >
//           Connect Drive
//         </button>
//       );

//     if (!isListVisible)
//       return (
//         <div className="flex flex-wrap gap-2">
//           <button
//             onClick={goBack}
//             className="px-3 py-1 text-sm text-white bg-gray-600 rounded hover:bg-gray-700"
//           >
//             ← Back
//           </button>
//           <button
//             onClick={listFiles}
//             className="px-3 py-1 text-sm text-white bg-indigo-600 rounded hover:bg-indigo-700"
//           >
//             Refresh
//           </button>
//           <button
//             onClick={() => setIsListVisible(true)}
//             className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
//           >
//             OPEN LIST
//           </button>
//         </div>
//       );

//     return (
//       <div className="flex flex-wrap gap-2">
//         <button
//           onClick={handleLogout}
//           className="px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700"
//         >
//           Logout
//         </button>
//         {folderStack.length > 0 && (
//           <button
//             onClick={goBack}
//             className="px-3 py-1 text-sm text-white bg-gray-600 rounded hover:bg-gray-700"
//           >
//             ← Back
//           </button>
//         )}
//         <button
//           onClick={listFiles}
//           className="px-3 py-1 text-sm text-white bg-indigo-600 rounded hover:bg-indigo-700"
//         >
//           Refresh
//         </button>
//         <button
//           onClick={createFolder}
//           className="px-3 py-1 text-sm text-white bg-green-600 rounded hover:bg-green-700"
//         >
//           + Folder
//         </button>
//         <button
//           onClick={() => document.getElementById("fileInput").click()}
//           className="px-3 py-1 text-sm text-white bg-yellow-500 rounded hover:bg-yellow-600"
//         >
//           Upload
//         </button>
//         <input id="fileInput" type="file" hidden onChange={handleFileChange} />
//       </div>
//     );
//   };

//   return (
//     <div className="flex w-full h-screen overflow-hidden bg-gray-50">
//       {isListVisible && <DriveMenu />}

//       <div className="flex flex-col flex-1 overflow-hidden">
//         <div className="flex flex-wrap items-center justify-between px-4 py-3 bg-white border-b shadow-sm">
//           <div>
//             <h2 className="text-lg font-semibold text-gray-700">
//               {isListVisible
//                 ? "Drive Explorer"
//                 : selectedFile?.name || "Drive Viewer"}
//             </h2>

//             {isListVisible && (
//               <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
//                 <input
//                   type="text"
//                   placeholder="Search by name..."
//                   value={searchName}
//                   onChange={(e) => setSearchName(e.target.value)}
//                   className="px-2 py-1 border rounded"
//                 />
//                 <input
//                   type="date"
//                   value={dateFrom}
//                   onChange={(e) => setDateFrom(e.target.value)}
//                   className="px-2 py-1 border rounded"
//                 />
//                 <input
//                   type="date"
//                   value={dateTo}
//                   onChange={(e) => setDateTo(e.target.value)}
//                   className="px-2 py-1 border rounded"
//                 />
//                 <button
//                   onClick={listFiles}
//                   className="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
//                 >
//                   Search
//                 </button>
//               </div>
//             )}
//           </div>

//           {renderTopButtons()}
//         </div>

//         {isListVisible ? (
//           <div className="flex flex-1 overflow-hidden">
//             <div className="w-[260px] bg-white border-r overflow-y-auto text-sm">
//               <div className="p-3 text-xs text-gray-500 truncate border-b">
//                 {breadcrumb}
//               </div>
//               {files.length > 0 ? (
//                 <ul>
//                   {files.map((file) => (
//                     <li
//                       key={file.id}
//                       onClick={() => setPreviewFile(file)}
//                       onDoubleClick={() => {
//                         if (
//                           file.mimeType ===
//                           "application/vnd.google-apps.folder"
//                         ) {
//                           openFolder(file.id, file.name);
//                         } else {
//                           setSelectedFile(file);
//                           setIsListVisible(false);
//                         }
//                       }}
//                       className={`px-4 py-2 border-b cursor-pointer truncate transition-all duration-150 ${
//                         previewFile?.id === file.id
//                           ? "bg-gray-200 text-gray-800 font-medium"
//                           : "hover:bg-gray-100 text-gray-600"
//                       }`}
//                     >
//                       <span className="flex items-center gap-1 truncate">
//                         {file.mimeType ===
//                         "application/vnd.google-apps.folder"
//                           ? "📁"
//                           : "📄"}{" "}
//                         {file.name}
//                       </span>
//                     </li>
//                   ))}
//                 </ul>
//               ) : (
//                 <div className="flex items-center justify-center h-full text-sm text-gray-400">
//                   No files found
//                 </div>
//               )}
//             </div>

//             <div className="flex-1 overflow-hidden bg-gray-100">
//               {files.length === 0 ? (
//                 <div className="flex items-center justify-center h-full text-gray-400">
//                   No files in this folder
//                 </div>
//               ) : (
//                 renderPreviewFile(previewFile)
//               )}
//             </div>
//           </div>
//         ) : (
//           <div className="flex items-center justify-center flex-1 bg-gray-100">
//             {renderFilePreview()}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DriveHome;